import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// OpenLayers
import proj4 from "proj4";
import { register as OlRegister } from "ol/proj/proj4";
import { get as OlGetProjection } from "ol/proj";
import OlMapBrowserEvent from "ol/MapBrowserEvent";
import OlBaseLayer from "ol/layer/Base";
import OlLayerGroup from "ol/layer/Group";
import OlCollection from "ol/Collection";
import OlSourceVector from "ol/source/Vector";
import Geometry from "ol/geom/Geometry";
import OlFeature from "ol/Feature";
import OlFormatWKT from "ol/format/WKT";

// Custom
import Map from "@/components/Map/Map";
import {
  Controls,
  RotateControl,
  ScaleLineControl,
  ZoomControl
} from "@/components/Map/Controls";

import { Layers, VectorLayer } from "@/components/Map/Layers";

import GeoBaseLayerSwitcher from "@/components/Map/Controls/GEO/GeoBaseLayerSwticher";
import GeoAPILayers from "@/components/Map/Layers/GEO/GeoAPILayers";
import UserContext from "@/components/UserContext/UserContext";
import { padExtent, flattenLayers } from "@/lib/olHelpers";

import dataController from "@/lib/dataController";

// Services
import mapService from "@/services/mapService";
import gsService from "@/services/gsService";

import { selectedStyle, commentsStyle } from "@/components/Map/mapStyles";
import { DCRecord } from "@/@types/lib/dataController";

// Types
import { IModel } from "@/@types/models/model";
import { ViewOptionsType } from "@/@types/common";

import MapButton from "@/views/DataCentric/MapButton"

interface IDataCentricMiniMapProps {
  selectedPrimaryRecordId: number;
  onPrimaryRecordSelect: Function;

  model: IModel;
  mapId: number;
  baseRecordPath: string
  geomRelativePath: string

  miniMapBaseRecordQueryParams?: string

  onResize?: () => void;
  isResized?: boolean;

  url: string;
}

function DataCentricMiniMap(props: IDataCentricMiniMapProps) {
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const [mapInitialized, setMapInitialized] = useState(false);

  // @ts-ignore
  const { selectedPrimaryRecordId, onPrimaryRecordSelect } = props;
  const { model, mapId, baseRecordPath, geomRelativePath, miniMapBaseRecordQueryParams, onResize, isResized, url } = props;

  const [defaultExtent, setDefaultExtent] = useState(
    userContext?.mapSettings?.default_extent
      ? userContext.mapSettings.default_extent
      : [1437016, 5271097, 2206278, 5860579]
  );

  const [recordExtent, setRecordExtent] = useState<number[]>([]);
  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center: userContext?.mapSettings ? userContext.mapSettings.initial_view_center : [1731757, 5581737],
    zoom: userContext?.mapSettings ? userContext.mapSettings.initial_view_zoom : 7,
    // extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    extent: [1447153, 5104226, 2162937, 5867310],
    constrainOnlyCenter: true,
    // projection: wgs84PM,
    minZoom: 7,
    maxZoom: 18,
    constrainResolution: true
  });
  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [highlightedSource, setHighlightedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [primaryRecords, setPrimaryRecords] = useState<DCRecord[]>([])

  let highlightedFeature: OlFeature<Geometry> | null = null;

  // define proj
  proj4.defs(
    "EPSG:3765",
    "+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs"
  );
  proj4.defs("EPSG:4326", "+proj=longlat +datum=WGS84 +no_defs");
  proj4.defs(
    "EPSG:31276",
    "+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs"
  );
  proj4.defs(
    "EPSG:3857",
    "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs"
  );
  OlRegister(proj4);

  const htrs96 = OlGetProjection("EPSG:3765");
  const wgs84 = OlGetProjection("EPSG:4326");
  const wgs84PM = OlGetProjection("EPSG:3857");

  const dc = new dataController(model);

  useEffect(() => {
    let isMounted = true;

    getLayers();
    refreshPrimaryRecords();

    function refreshPrimaryRecords() {
      dc.GetData(`${baseRecordPath}${miniMapBaseRecordQueryParams ?? ""}`).then((resp) => {
        if (!isMounted) return;
        if (resp.success) {
          const records = resp.data as DCRecord[];
          if (Array.isArray(records) && records.length > 0) {
            // filter when .geom is missing
            const filteredRecords = records.filter((record) => {
              return record.hasOwnProperty('geom') && record.geom && record.geom !== null;
            });
            if (isMounted) {
              setPrimaryRecords(filteredRecords);
            }
          } else {
            if (isMounted) {
              setPrimaryRecords([]);
              setRecordExtent([]);
            }
          }
        }
      });
    };

    return () => {
      isMounted = false;
    };
  }, []);


  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const clearMap = () => {
    setHighlightedSource(new OlSourceVector({ features: [] }));
    setSelectedSource(new OlSourceVector({ features: [] }));
    setRecordExtent([]);
  }


  useEffect(() => {
    let isMounted = true;

    if (selectedPrimaryRecordId > 0) {
      const rec = primaryRecords.find((x) => x.id === selectedPrimaryRecordId);

      if (rec && rec.geom && isMounted) { // check if we already have geometry
        const features = primaryRecordsSource?.getFeatures().filter(x => x.getId() === selectedPrimaryRecordId);
        const srcVector = new OlSourceVector({ features: features });
        setSelectedSource(srcVector);
        setRecordExtent(srcVector.getExtent() as number[]);
      } else { // if we don't, try to fetch it
        if (!isMounted) return;
        const geomUrl = `${baseRecordPath}/${selectedPrimaryRecordId}/${geomRelativePath}` as string
        dc.GetData(geomUrl).then((resp) => {
          if (resp.success) {
            const dcRec = resp.data as DCRecord;
            const wkt = dcRec?.wkt as string;
            let proj = dcRec?.proj as string;
            proj = Number.isInteger(proj) ? "EPSG:".concat(proj as string) : proj;
            if (wkt && proj) {

              const wktFormatter = new OlFormatWKT();
              const feat = wktFormatter.readFeature(wkt, {
                dataProjection: proj,
                featureProjection: "EPSG:3857"
              })
              if (isMounted) {
                setSelectedSource(new OlSourceVector({ features: [feat] }));
                const geom = feat.getGeometry();
                if (geom) {
                  setRecordExtent(geom.getExtent())
                }
              }
            } else {
             clearMap();
            }
          } else {
            if (isMounted) {
             clearMap();
            }
          }
        });
      }
    } else {
      if (isMounted) {
        clearMap();
      }
    }

    return () => {
      isMounted = false;
    };
  }, [selectedPrimaryRecordId]);

  useEffect(() => {
    if (selectedSource) {
      const features = selectedSource.getFeatures();
      if (features.length > 0) {
        setRecordExtent(selectedSource.getExtent())
      }
    }
  }, [selectedSource])

  const primaryRecordsSource = useMemo(() => {
    if (primaryRecords === null) return;
    const features = primaryRecords.map((x) => x.geom).filter(x => x !== undefined);
    // @ts-ignore
    return new OlSourceVector({ features: features as OlFeature<Geometry>[] });
  }, [primaryRecords])


  const handleClick = (evt: OlMapBrowserEvent<any>) => {
    var feature = evt.map.forEachFeatureAtPixel(
      evt.pixel,
      function (feature) {
        return feature;
      },
      {
        layerFilter: (layer) => {
          const layerId = layer.get("id");
          return layerId !== null && layerId !== undefined && layerId.startsWith("primary-records");
        }
      }
    );

    if (feature) {
      const id = feature.getId();
      onPrimaryRecordSelect(id);

    } else {

      const allLayers = layersCollection
        ? flattenLayers(layersCollection.getArray()).filter(
          (x) => !(x instanceof OlLayerGroup)
        )
        : [];

      const GSLayerNames = allLayers
        .filter((x) => x.get("query") === true)
        .map((x) => x.get("layer"));

      if (GSLayerNames.length > 0) {
        gsService.getFeatureInfo(evt.map, evt.pixel, GSLayerNames).then((resp) => {
          if (resp && Object.keys(resp).length != 0) {
            const layerKeys = Object.keys(resp);
            const key = layerKeys.length > 0 ? layerKeys[0] : null;
            const features = key ? resp[key] : [];

            if (Array.isArray(features) && features.length > 0) {
              const feat = features[0];
              const id = feat.properties["id"];
              onPrimaryRecordSelect(id, feat.properties as DCRecord);

            } else {
              onPrimaryRecordSelect(null);
            }
          } else {
            onPrimaryRecordSelect(null);
          }
        });
      } else {
        onPrimaryRecordSelect(null);
      }
    }
  };

  const handlePointerMove = (evt: OlMapBrowserEvent<any>) => {
    if (evt.dragging) {
      return;
    }
    const pixel = evt.map.getEventPixel(evt.originalEvent);
    const feature = evt.map.forEachFeatureAtPixel(
      pixel,
      function (feature: any) {
        return feature;
      },
      {
        layerFilter: (layer) => {
          // const layerId = layer.get("id");
          // return layerId !== null && layerId !== undefined && layerId.startsWith("primary-records");
          return true;
        }
      }
    ) as unknown as OlFeature<Geometry>;
    highlightFeature(feature);
  };


  const highlightFeature = (feature: OlFeature<Geometry>) => {
    const olFeature = feature;
    if (olFeature !== highlightedFeature) {
      if (highlightedSource) {
        if (highlightedFeature) {
          highlightedSource.removeFeature(highlightedFeature);
        }
        if (olFeature) {
          highlightedSource.addFeature(olFeature);
        }
      }
      highlightedFeature = olFeature;
    }
  };

  const handleClickResize = () => {
    if (onResize) {
      onResize();
      if (url) {
        saveExtendedMapToLocalStorage();
      }
    }
  }

  const saveExtendedMapToLocalStorage = () => {
    try {
      const extendedMaps = localStorage.getItem("extended_mini_map")
      if (extendedMaps === null) {
        localStorage.setItem("extended_mini_map", JSON.stringify([url]))
      } else {
        let array = JSON.parse(extendedMaps);
        if (!isResized) {
          let found = false;
          array.forEach((element: string) => {
            if (element.valueOf() === url.valueOf()) {
              found = true;
              return;
            }
          });

          if (!found) {
            array.push(url)
            localStorage.setItem("extended_mini_map", JSON.stringify(array));
          }
        } else {
          array = array.filter((item: string) => item.valueOf() !== url.valueOf())
          localStorage.setItem("extended_mini_map", JSON.stringify(array));
        }

      }
    } catch (error) {
      // TODO
    }
  }

  return (layersCollection ? (
    <Map
      mapId={mapId}
      height="500px"
      view={viewOptions}
      onClick={handleClick}
      onPointermove={handlePointerMove}
      className="sidebar-map"
      id="mini-map"
      zoomToExtent={recordExtent.length ? recordExtent : undefined}
      zoomToExtentPadding={[20, 20, -20, -20]}
      initialized={mapInitialized}
    >
      <Controls>
        <ZoomControl zoomInTipLabel={t("map:controls.zoom_in")} zoomOutTipLabel={t("map:controls.zoom_out")} />

        <MapButton id="button-resize" className="ol-button-resize" handleClick={handleClickResize}>
          <i className={isResized ? 'fas fa-angle-double-down' : 'fas fa-angle-double-up'} />
        </MapButton>

        <RotateControl autoHide={false} />
        <ScaleLineControl ignoreSidebar />
        <GeoBaseLayerSwitcher mapId={mapId} allowNoLayer={false} isMini />
      </Controls>
      <Layers>
        <GeoAPILayers layersCollection={layersCollection} />
        <VectorLayer id="primary-records" source={primaryRecordsSource} style={commentsStyle} zIndex={900} />
        <VectorLayer id="selected" source={selectedSource} style={selectedStyle} zIndex={950} />
      </Layers>
    </Map>
  ) : null
  );
}

export default DataCentricMiniMap;
